import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog';
import { BookOpen, FileText, ChevronDown } from 'lucide-react';

const reportNames = {
  'executive-summary': 'Executive Summary',
  'pnl-by-symbol': 'P&L by Symbol',
  'client-profitability': 'Client Profitability',
  'commission-analytics': 'Commission Analytics',
  'deposit-withdrawal': 'Deposit & Withdrawal Flow',
  'book-analysis': 'A/B/C Book Analysis',
  'hedging-efficiency': 'Hedging Efficiency',
  'regulatory-capital': 'Regulatory Capital',
  'suspicious-activity': 'Suspicious Activity (AML)',
  'margin-calls': 'Margin Call Monitoring',
  'financial-statement': 'Financial Statement (P&L)',
  'balance-sheet': 'Balance Sheet',
  'execution-quality': 'Trade Execution Quality',
  'exit-valuation': 'Exit Valuation Calculator',
  'investor-report': 'Investor Due Diligence',
  'daily-funding': 'Daily Deposits & Withdrawals',
  'dividend-inout': 'Dividend In/Out'
};

const GlossaryDialog = ({ glossary }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('abbreviations');
  
  if (!glossary) return null;
  
  const filteredTerms = Object.entries(glossary.terms || {}).filter(
    ([key, value]) => key.toLowerCase().includes(searchTerm.toLowerCase()) || 
                      value.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredAbbrev = Object.entries(glossary.abbreviations || {}).filter(
    ([key, value]) => key.toLowerCase().includes(searchTerm.toLowerCase()) || 
                      value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMethodologies = Object.entries(glossary.report_descriptions || {}).filter(
    ([key, value]) => key.toLowerCase().includes(searchTerm.toLowerCase()) || 
                      value.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="glossary-btn">
          <BookOpen className="w-4 h-4" />
          Glossary
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-cyan-400" />
            Glossary, Terms & Report Methodologies
          </DialogTitle>
          <DialogDescription>
            Complete reference guide for financial terms and report documentation
          </DialogDescription>
        </DialogHeader>
        
        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          <Button 
            variant={activeTab === 'abbreviations' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setActiveTab('abbreviations')}
          >
            Abbreviations ({Object.keys(glossary.abbreviations || {}).length})
          </Button>
          <Button 
            variant={activeTab === 'terms' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setActiveTab('terms')}
          >
            Terms ({Object.keys(glossary.terms || {}).length})
          </Button>
          <Button 
            variant={activeTab === 'methodologies' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setActiveTab('methodologies')}
          >
            Report Methodologies ({Object.keys(glossary.report_descriptions || {}).length})
          </Button>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder={`Search ${activeTab}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
        />
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto pr-2">
          {/* Abbreviations Tab */}
          {activeTab === 'abbreviations' && (
            <div className="py-4">
              <div className="grid grid-cols-2 gap-3">
                {filteredAbbrev.map(([abbr, full]) => (
                  <div key={abbr} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                    <Badge variant="outline" className="font-mono text-cyan-400">{abbr}</Badge>
                    <span className="text-sm text-muted-foreground">{full}</span>
                  </div>
                ))}
              </div>
              {filteredAbbrev.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No abbreviations found matching &quot;{searchTerm}&quot;</p>
              )}
            </div>
          )}

          {/* Terms Tab */}
          {activeTab === 'terms' && (
            <div className="py-4 space-y-3">
              {filteredTerms.map(([term, definition]) => (
                <div key={term} className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="font-semibold text-cyan-400 mb-2">{term}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{definition}</p>
                </div>
              ))}
              {filteredTerms.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No terms found matching &quot;{searchTerm}&quot;</p>
              )}
            </div>
          )}

          {/* Methodologies Tab */}
          {activeTab === 'methodologies' && (
            <div className="py-4 space-y-4">
              {filteredMethodologies.map(([key, content]) => {
                const lines = content.split('\n');
                const title = lines[0] || reportNames[key] || key;
                const body = lines.slice(1).join('\n');
                
                return (
                  <details key={key} className="group">
                    <summary className="cursor-pointer p-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-cyan-400" />
                        <div>
                          <p className="font-semibold">{reportNames[key] || key}</p>
                          <p className="text-xs text-muted-foreground">{key}</p>
                        </div>
                      </div>
                      <ChevronDown className="w-5 h-5 text-muted-foreground group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="mt-2 p-4 rounded-lg bg-black/30 border border-white/5">
                      <h4 className="text-cyan-400 font-bold mb-3">{title}</h4>
                      <pre className="whitespace-pre-wrap text-sm font-sans text-muted-foreground leading-relaxed">
                        {body}
                      </pre>
                    </div>
                  </details>
                );
              })}
              {filteredMethodologies.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No methodologies found matching &quot;{searchTerm}&quot;</p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GlossaryDialog;
